<template>
    <div>
        <div class="buttons are-small">
            <template v-if="choosingMany">
                <button class="button toggle-event-registrations" @click="toggleSelections">
                    <span class="icon">
                        <i class="fa fa-check-square" v-if="selectedAll" />
                        <i class="fa fa-square" v-else />
                    </span>
                </button>

                <button class="button" @click="cancelSelectingMany">
                    <span class="icon">
                        <i class="fa fa-times" />
                    </span>
                    <span>{{ translate("common.cancel") }}</span>
                </button>

                <template v-if="bulkModeIsAnySelected">
                    <button class="button is-success" @click="bulkUpdateCompetitorPayments(true)">
                        {{ translate("admin_payment.mark_all_complete") }}
                    </button>
                    <button class="button is-warning" @click="bulkUpdateCompetitorPayments(false)">
                        {{ translate("admin_payment.mark_all_incomplete") }}
                    </button>
                </template>
            </template>
            <button v-else class="button" @click="startSelectingMany">
                <span class="icon">
                    <i class="fa fa-tasks" />
                </span>
                <span>{{ translate("admin_payment.select_many") }}</span>
            </button>
        </div>
        <table class="table is-condensed is-hoverable is-fullwidth">
            <thead>
                <tr>
                    <th>#</th>
                    <th>{{ translate("common.first_name_and_last_name") }}</th>
                    <th>{{ translate("common.category") }}</th>
                    <th>{{ translate("common.registration_date") }}</th>
                    <th>{{ translate("admin_payments.payment_status") }}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(competitorPayment, index) in competitorPayments" :key="competitorPayment.id">
                    <td>
                        <input
                            type="checkbox"
                            v-if="choosingMany && isChangeIsAllowed(competitorPayment.payment.status)"
                            v-model="bulkUpdateSelections[competitorPayment.id]"
                        />
                        <span v-else>{{ index + 1 }}</span>
                    </td>
                    <td class="clarity-mask">{{ competitorPayment.firstName }} {{ competitorPayment.lastName }}</td>
                    <td>{{ competitorPayment.category }}</td>
                    <td>
                        <RegistrationStageNumber
                            :stage-number="competitorPayment.stageNumber"
                            :date="competitorPayment.creationDate"
                            :timezone="eventTimezone"
                        />
                    </td>
                    <td style="min-width: 267px">
                        <div class="field is-grouped" v-if="editId === competitorPayment.id">
                            <div class="control">
                                <input class="input fee-input is-small" type="text" v-model="amount" />
                            </div>
                            <div class="control">
                                <div class="checkbox">
                                    <label>
                                        <input type="checkbox" v-model="isComplete" />
                                        {{ translate("admin_payment.payment_complete") }}
                                    </label>
                                </div>
                            </div>
                        </div>
                        <CompetitorPaymentStatus class="are-small" v-else :competitor="competitorPayment" />
                    </td>
                    <td>
                        <template v-if="editId === competitorPayment.id">
                            <div class="buttons">
                                <LoadingButton
                                    class="button is-success is-small"
                                    @click="confirmEdit"
                                    :disabled="amountIsValidValue"
                                    :is-loading="updatingInProgress === competitorPayment.id"
                                >
                                    {{ translate("common.ok") }}
                                </LoadingButton>
                                <button class="button is-danger is-small" @click="cancelEdit">
                                    {{ translate("common.cancel") }}
                                </button>
                            </div>
                        </template>
                        <template v-else>
                            <div class="buttons are-small">
                                <button
                                    class="button is-light"
                                    :disabled="!competitorPayment.paymentDocument"
                                    @click="onOpenPaymentDocument(competitorPayment.paymentDocument?.id)"
                                >
                                    <span class="icon">
                                        <i class="fa fa-file-invoice-dollar"></i>
                                    </span>
                                </button>

                                <button
                                    class="button"
                                    @click="editCompetitorPayment(competitorPayment)"
                                    :disabled="isChangingSingleRowAllowed(competitorPayment)"
                                >
                                    {{ translate("common.change") }}
                                </button>
                            </div>
                        </template>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script setup lang="ts">
    import { ref, computed } from "vue";
    import CompetitorPaymentStatus from "./CompetitorPaymentStatus.vue";
    import RegistrationStageNumber from "./RegistrationStageNumber.vue";
    import { eventBus, OPEN_PAYMENT_DOCUMENT } from "../eventBus";
    import { PAYMENT_STATUS_ONLINE_COMPLETE, PAYMENT_STATUS_ONLINE_PROCESSING, PaymentStatus } from "../../Common/consts";
    import paymentsApi, { CompetitorPaymentWithMetadata } from "../../Api/payments";
    import { translate } from "../../Common/translations";
    import { successToast } from "../../Common/toasts";
    import { handleError } from "../../Common/errorHandler";
    import urls from "../../Urls/urls";
    import LoadingButton from "../../Common/LoadingButton.vue";

    // Props
    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
        competitorPayments: CompetitorPaymentWithMetadata[];
        user: any;
    }>();

    // State
    const updatingInProgress = ref(0);
    const editId = ref(0);
    const amount = ref(0);
    const isComplete = ref(false);
    const choosingMany = ref(false);
    const selectedAll = ref(false);
    const bulkUpdateSelections = ref<Record<number, boolean>>({});

    //Emit
    const emit = defineEmits<{
        (event: "competitorPaymentsUpdated", updatedCompetitorPayments: CompetitorPaymentWithMetadata[]): void;
    }>();

    // Computed
    const bulkModeIsAnySelected = computed(() => Object.keys(bulkUpdateSelections.value).some(key => !!bulkUpdateSelections.value[key]));
    const amountIsValidValue = computed(() => !/^[0-9]+$/.test(String(amount.value)));

    const isChangingSingleRowAllowed = (competitorPayment: CompetitorPaymentWithMetadata): boolean => {
        return (
            choosingMany.value ||
            !isChangeIsAllowed(competitorPayment.payment.status) ||
            (editId.value > 0 && editId.value !== competitorPayment.id) //If something else is selected already, change is blocked.
        );
    };

    function updateCompetitorPayment(eventCompetitorId: number) {
        return paymentsApi.updateCompetitorPayment(props.eventId, eventCompetitorId, amount.value, isComplete.value);
    }

    function bulkUpdateCompetitorPayments(complete: boolean) {
        const eventRegistrationIds = Object.keys(bulkUpdateSelections.value)
            .filter(id => bulkUpdateSelections.value[Number(id)])
            .map(Number);

        return paymentsApi
            .bulkUpdateCompetitorPayments(props.eventId, {
                searchCriteria: { eventRegistrationIds },
                complete,
                timestamp: new Date().toISOString(),
            })
            .then(() => fetchCompetitorPaymentsForUser(props.user.id))
            .then(updatedCompetitorPayments => {
                emit("competitorPaymentsUpdated", updatedCompetitorPayments);
                successToast(translate("admin_payments.competitor_updated"));
            })
            .catch(handleError)
            .finally(() => {
                resetSelectingMany();
                choosingMany.value = false;
            });
    }

    function editCompetitorPayment(competitorPayment: any) {
        editId.value = competitorPayment.id;
        amount.value = competitorPayment.payment.amount;
        isComplete.value = competitorPayment.payment.is_complete;
    }

    function confirmEdit() {
        updatingInProgress.value = editId.value;
        return updateCompetitorPayment(editId.value)
            .then(() => fetchCompetitorPaymentsForUser(props.user.id))
            .then(updatedCompetitorPayments => {
                emit("competitorPaymentsUpdated", updatedCompetitorPayments);
                successToast(translate("admin_payments.competitor_updated"));
            })
            .catch(handleError)
            .finally(() => {
                updatingInProgress.value = 0;
                editId.value = 0;
                amount.value = 0;
                isComplete.value = false;
            });
    }

    function cancelEdit() {
        amount.value = 0;
        isComplete.value = false;
        editId.value = 0;
    }

    function fetchCompetitorPaymentsForUser(userId: number) {
        return paymentsApi.getCompetitorPaymentsPage(props.eventId, { offset: 0, limit: 1000, userId }).then(response => response.result);
    }

    function startSelectingMany() {
        resetSelectingMany();
    }

    function toggleSelections() {
        props.competitorPayments.forEach(competitorPayment => {
            bulkUpdateSelections.value[competitorPayment.id] = !selectedAll.value;
        });
        selectedAll.value = !selectedAll.value;
    }

    function resetSelectingMany() {
        Object.keys(bulkUpdateSelections.value).forEach(key => delete bulkUpdateSelections.value[Number(key)]);
        selectedAll.value = false;
        choosingMany.value = true;
    }

    function cancelSelectingMany() {
        choosingMany.value = false;
    }

    function onOpenPaymentDocument(paymentDocumentId?: number) {
        if (!paymentDocumentId) {
            return;
        }

        const url = urls.userEventPagesUrls().paymentDocumentDownload(props.eventId, paymentDocumentId, false);
        eventBus.$emit(OPEN_PAYMENT_DOCUMENT, url);
    }

    function isChangeIsAllowed(paymentStatus: PaymentStatus) {
        return paymentStatus !== PAYMENT_STATUS_ONLINE_PROCESSING && paymentStatus !== PAYMENT_STATUS_ONLINE_COMPLETE;
    }
</script>

<style scoped>
    .fee-input {
        width: 70px;
    }
</style>
